import { useState } from "react";
import { SelectChangeEvent, useTheme } from "@mui/material";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import {
  SentToClockRange,
  useSentToClockStats,
} from "../../../components/hooks/useSentToClockStats";
import DSCard from "views/system-design/DSCard";
import DSCardContent from "views/system-design/DSCardContent";
import DSBox from "views/system-design/DSBox";
import DSStack from "views/system-design/DSStack";
import DSTypography from "views/system-design/DSTypography";
import DSTextField from "views/system-design/DSTextField";
import DSSelect from "views/system-design/DSSelect";
import DSMenuItem from "views/system-design/DSMenuItem";
import DSButton from "views/system-design/DSButton";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import {
  DSTable,
  DSTableHead,
  DSTableBody,
  DSTableRow,
  DSTableCell,
} from "views/system-design/Table";

const PRESET_DAYS = [7, 30, 90] as const;

// Matches the API's cap. Enforced here too so a too-wide range is not offered at
// all, rather than being sent and rejected; the card's error state stays as the
// backstop for anything else that fails.
const MAX_RANGE_DAYS = 365;

const spanInDays = (from: string, to: string) =>
  (Date.parse(to) - Date.parse(from)) / (1000 * 60 * 60 * 24);

const todayStr = () => new Date().toISOString().slice(0, 10);

const isCustom = (
  range: SentToClockRange,
): range is { from: string; to: string } => "from" in range;

const formatDate = (iso: string) => {
  if (!iso) return "—";
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime())
    ? "—"
    : parsed.toLocaleDateString(undefined, {
        year: "2-digit",
        month: "short",
        day: "numeric",
      });
};

type StatTileProps = {
  label: string;
  value: string;
};

function StatTile({ label, value }: StatTileProps) {
  return (
    <DSBox sx={{ flex: 1, minWidth: 0, textAlign: "center" }}>
      <DSTypography variant="h4" sx={{ m: 0 }}>
        {value}
      </DSTypography>
      <DSTypography variant="body2" sx={{ color: "text.secondary", m: 0 }}>
        {label}
      </DSTypography>
    </DSBox>
  );
}

/**
 * Which artists used the studio's "Send to Clock" on their own klydos and
 * collections.
 *
 * Admin and viewer accounts never appear: the studio skips analytics for them,
 * so this is real artist usage only.
 */
function SentToClockWidget() {
  const theme = useTheme();
  // The applied range that actually drives the query.
  const [range, setRange] = useState<SentToClockRange>({ days: 30 });
  // Whether the custom date-range editor is shown.
  const [customMode, setCustomMode] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const { data: stats, isLoading, isError } = useSentToClockStats(range);

  const selectValue =
    customMode || isCustom(range) ? "custom" : String(range.days);

  const handlePresetChange = (e: SelectChangeEvent<unknown>) => {
    const value = e.target.value as string;
    if (value === "custom") {
      setCustomMode(true);
      return;
    }
    setCustomMode(false);
    setRange({ days: Number(value) });
  };

  const applyCustomRange = () => {
    if (from && to && from <= to) {
      setRange({ from, to });
    }
  };

  const isRangeTooWide =
    !!from && !!to && from <= to && spanInDays(from, to) > MAX_RANGE_DAYS;
  const canApply = !!from && !!to && from <= to && !isRangeTooWide;
  const numericCellSx = { textAlign: "right" as const, whiteSpace: "nowrap" };
  const artists = stats?.artists ?? [];

  return (
    <DSCard sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <DSBox
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          pt: 2,
          gap: 1,
        }}
      >
        <DSStack direction="row" alignItems="center" spacing={1.5}>
          <SendOutlinedIcon sx={{ fontSize: "2rem", color: "#0288D1" }} />
          <DSTypography variant="h6" sx={{ m: 0 }}>
            Sent To Own Clock
          </DSTypography>
        </DSStack>
        <DSStack
          direction="row"
          alignItems="center"
          spacing={1}
          flexWrap="wrap"
          justifyContent="flex-end"
        >
          <DSSelect
            size="small"
            value={selectValue}
            onChange={handlePresetChange}
            sx={{ minWidth: 130 }}
          >
            {PRESET_DAYS.map((d) => (
              <DSMenuItem key={d} value={String(d)}>
                Last {d} days
              </DSMenuItem>
            ))}
            <DSMenuItem value="custom">Custom range</DSMenuItem>
          </DSSelect>
          {selectValue === "custom" && (
            <>
              <DSTextField
                type="date"
                size="small"
                label="From"
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: to || todayStr() }}
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                sx={{ width: 150 }}
              />
              <DSTextField
                type="date"
                size="small"
                label="To"
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: from, max: todayStr() }}
                value={to}
                onChange={(e) => setTo(e.target.value)}
                sx={{ width: 150 }}
              />
              <DSButton
                size="small"
                variant="contained"
                disabled={!canApply}
                onClick={applyCustomRange}
                title={
                  isRangeTooWide
                    ? `Range must not exceed ${MAX_RANGE_DAYS} days`
                    : undefined
                }
              >
                Apply
              </DSButton>
            </>
          )}
        </DSStack>
      </DSBox>
      <DSCardContent sx={{ flex: 1 }}>
        {isLoading ? (
          <DSBox sx={{ textAlign: "center", width: "100%", p: "20px" }}>
            <DSCircularProgress />
          </DSBox>
        ) : isError && !stats ? (
          // All-zero tiles would read as "nobody used the feature", so a failed
          // request has to say so instead.
          <DSTypography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", py: 3 }}
          >
            Failed to load send stats. Try again or pick a different range.
          </DSTypography>
        ) : (
          <>
            <DSStack
              direction="row"
              spacing={2}
              sx={{ width: "100%" }}
              divider={
                <DSBox
                  sx={{
                    width: "1px",
                    alignSelf: "stretch",
                    backgroundColor: theme.palette.divider,
                  }}
                />
              }
            >
              <StatTile
                label="Total sends"
                value={String(stats?.totalSends ?? 0)}
              />
              <StatTile
                label="Artists"
                value={String(stats?.uniqueArtists ?? 0)}
              />
              <StatTile
                label="Klydos / collections"
                value={`${stats?.klydoSends ?? 0} / ${
                  stats?.collectionSends ?? 0
                }`}
              />
            </DSStack>
            {artists.length === 0 ? (
              <DSTypography
                variant="body2"
                sx={{ color: "text.secondary", textAlign: "center", mt: 3 }}
              >
                No artist has sent content to their own clock in this range.
              </DSTypography>
            ) : (
              <DSBox sx={{ mt: 3, overflowX: "auto" }}>
                <DSTable size="small">
                  <DSTableHead>
                    <DSTableRow>
                      <DSTableCell>Artist</DSTableCell>
                      <DSTableCell sx={numericCellSx}>Klydos</DSTableCell>
                      <DSTableCell sx={numericCellSx}>Collections</DSTableCell>
                      <DSTableCell sx={numericCellSx}>Last sent</DSTableCell>
                    </DSTableRow>
                  </DSTableHead>
                  <DSTableBody>
                    {artists.map((artist) => (
                      <DSTableRow key={artist.userId}>
                        <DSTableCell>{artist.artistName}</DSTableCell>
                        <DSTableCell sx={numericCellSx}>
                          {artist.klydoSends}
                        </DSTableCell>
                        <DSTableCell sx={numericCellSx}>
                          {artist.collectionSends}
                        </DSTableCell>
                        <DSTableCell sx={numericCellSx}>
                          {formatDate(artist.lastSentAt)}
                        </DSTableCell>
                      </DSTableRow>
                    ))}
                  </DSTableBody>
                </DSTable>
              </DSBox>
            )}
          </>
        )}
      </DSCardContent>
    </DSCard>
  );
}

export default SentToClockWidget;
